import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { KeyRound, Loader2, Check, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/super-admin';

const SuperAdminForcePasswordChange = ({ token, admin, onPasswordChanged }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Password validation
  const hasMinLength = newPassword.length >= 8;
  const hasNumber = /\d/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const isValid = hasMinLength && hasNumber && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isValid) {
      setError('Please ensure all password requirements are met');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/auth/force-change-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onPasswordChanged();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700" data-testid="super-admin-force-password-change-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl overflow-hidden bg-amber-500/20 flex items-center justify-center">
              <KeyRound className="h-8 w-8 text-amber-500" />
            </div>
          </div>
          <CardTitle className="text-2xl text-white">Change Your Password</CardTitle>
          <CardDescription className="text-slate-400">
            Welcome, {admin?.full_name || admin?.username}! Please set a new password to continue.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4 bg-red-500/20 border-red-500/50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">New Password</Label>
              <div className="relative">
                <Input
                  data-testid="new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-slate-700/50 border-slate-600 text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Confirm New Password</Label>
              <Input
                data-testid="confirm-password-input"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-slate-700/50 border-slate-600 text-white"
                required
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-900/50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-medium text-slate-400 mb-2">Password Requirements:</p>
              <div className="flex items-center gap-2 text-sm">
                {hasMinLength ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <div className="h-4 w-4 rounded-full border border-slate-600" />
                )}
                <span className={hasMinLength ? 'text-green-500' : 'text-slate-500'}>
                  At least 8 characters
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {hasNumber ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <div className="h-4 w-4 rounded-full border border-slate-600" />
                )}
                <span className={hasNumber ? 'text-green-500' : 'text-slate-500'}>
                  Contains at least one number
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {passwordsMatch ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <div className="h-4 w-4 rounded-full border border-slate-600" />
                )}
                <span className={passwordsMatch ? 'text-green-500' : 'text-slate-500'}>
                  Passwords match
                </span>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#ffd108] hover:bg-[#e5bc07] text-black font-semibold" 
              disabled={loading || !isValid}
              data-testid="change-password-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Changing Password...
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4 mr-2" />
                  Set New Password
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperAdminForcePasswordChange;
